import Model from './Model';
import PropertyDescriptor from '../utils/PropertyDescriptor';
import Envelope from "../utils/Envelope";
import ModelThicknessProperty from "./ModelThicknessProperty";
import ModelCssLengthProperty from "./ModelCssLengthProperty";

export default class TransformModel extends Model {

    layoutMode = new PropertyDescriptor('absolute', '定位方式', "布局", "enum", "#anticon-MasterLayout", {
        gallery: "外观",
        galleryIndex: 70,
        values: [
            new PropertyDescriptor("absolute", "绝对定位", "布局方式", "string"),
            new PropertyDescriptor("flex", "线性布局", "布局方式", "string"),
        ],
        description: "绝对定位通过 x 和 y 属性控制位置；线性布局将根据父部件的布局方向（垂直/水平）进行自动布局",
    });

    flex = new PropertyDescriptor(-1, "线性占比", "布局", "double", "#anticon-bilibang", {
        gallery: "外观",
        minValue: -1,
        maxValue: 100,
        description: "部件占父部件线性布局长度的百分比",
        visible: false,
    });

    x = new PropertyDescriptor(0, "X 坐标", "布局", "double", "#anticon-X", {
        gallery: "外观",
        catalogIndex: 30
    });
    y = new PropertyDescriptor(0, "Y 坐标", "布局", "double", "#anticon-Y", { gallery: "外观", });
    width = new PropertyDescriptor(new ModelCssLengthProperty(100), "宽度", "布局", "css-length", "#anticon-fit-width", {
        gallery: "外观",
        valueTemplate: ModelCssLengthProperty
    });
    height = new PropertyDescriptor(new ModelCssLengthProperty(100), "高度", "布局", "css-length", "#anticon-fit-height", {
        gallery: "外观",
        valueTemplate: ModelCssLengthProperty
    });
    zIndex = new PropertyDescriptor(-1, "层级", "布局", "double", "#anticon-layer6", {
        description: "当多个部件重叠时，您可以使用该参数控制部件的重叠顺序，值越大越处于上层。",
    });
    margin = new PropertyDescriptor(new ModelThicknessProperty(0), '外边距', "布局", "thickness", "#anticon-waibianju", {
        gallery: "外观",
        valueTemplate: ModelThicknessProperty
    });
    padding = new PropertyDescriptor(new ModelThicknessProperty(0), '内边距', "布局", "thickness", "#anticon-neibianju", {
        gallery: "外观",
        valueTemplate: ModelThicknessProperty
    });
    scale = new PropertyDescriptor(1, "缩放比例", "布局", "double", "#anticon-zoom-", {
        // gallery: "外观",
        enabled: false,
        minValue: 0,
    });

    onPropertyValueChanged(name, valOld, valNew, subName, args) {
        super.onPropertyValueChanged(name, valOld, valNew, subName, args);

        if (name === "layoutMode" && subName === undefined) {
            if (valNew === "absolute") {
                this.setPropertyVisible("x", true);
                this.setPropertyVisible("y", true);
                this.setPropertyVisible("flex", false);
            }
            else if (valNew === "flex") {
                this.setPropertyVisible("x", false);
                this.setPropertyVisible("y", false);
                this.setPropertyVisible("flex", true);
            }
        }
    }

    enableTransformModelProperties(enabled) {
        this.scale.enabled = enabled;
        this.padding.enabled = enabled;
        this.margin.enabled = enabled;
        this.zIndex.enabled = enabled;
        this.height.enabled = enabled;
        this.width.enabled = enabled;
        this.x.enabled = enabled;
        this.y.enabled = enabled;
        this.flex.enabled = enabled;
        this.layoutMode.enabled = enabled;
    }

    defineAllProperties() {
        super.defineAllProperties();
        this.raisePropertyValueChanged("layoutMode", this.layoutMode, this.layoutMode, undefined);
    }

    toStyle() {
        return {
        };
    }

    getViewScale() {
        let parentScale = this._sys.parent ? this._sys.parent.getViewScale() : 1;
        return parentScale * this.scale;
    }

    getViewLocation(opts = { withPaperBorder: true }) {

        let parentScale = this._sys.parent ? this._sys.parent.getViewScale() : 1;
        let parentLocation = this._sys.parent ? this._sys.parent.getViewLocation(opts) : null;
        // let parentPadding = this._sys.parent ? this._sys.parent.padding : (this._sys.designer ? this._sys.designer.paper.padding : 0);
        // let parentMargin = this._sys.parent ? this._sys.parent.margin : (this._sys.designer ? this._sys.designer.paper.margin : 0);
        let parentBorderWidth = this._sys.parent ? this._sys.parent.borderWidth : (this._sys.designer && opts.withPaperBorder ? this._sys.designer.paper.borderWidth : 0);

        let offsetLeft = 0;
        let offsetTop = 0;

        let dom = this._sys.dom
        if (dom) {
            do {
                offsetLeft += dom.offsetLeft;
                offsetTop += dom.offsetTop;

                dom = dom.parentElement;

            } while (dom && this._sys.parent && this._sys.parent._sys.dom && this._sys.parent._sys.dom !== dom);
        }

        return this._sys.dom ?
            {
                x: (offsetLeft + parentBorderWidth) * parentScale + (parentLocation ? parentLocation.x : 0),
                y: (offsetTop + parentBorderWidth) * parentScale + (parentLocation ? parentLocation.y : 0),
            } :
            {
                x: 0,
                y: 0,
            }
    }

    getViewSize() {

        const scale = this.getViewScale();

        return this._sys.dom ?
            {
                width: this._sys.dom.offsetWidth * scale,
                height: this._sys.dom.offsetHeight * scale,
            } :
            {
                width: 0,
                height: 0
            }
    }

    findIntersects(x, y, tolerance = 0) {

        if (this.isFindable && !this.isFindable())
            return null;

        const location = this.getViewLocation();
        const size = this.getViewSize();

        return new Envelope({ left: location.x, top: location.y, width: size.width, height: size.height }).intersectsByXY(x, y, tolerance) ? this : null;
    }

    intersects(x, y, tolerance = 0) {

        const location = this.getViewLocation();
        const size = this.getViewSize();

        return new Envelope({ left: location.x, top: location.y, width: size.width, height: size.height }).intersectsByXY(x, y, tolerance);
    }

    intersectsByRectangle(x, y, width, height, tolerance = 0) {

        const location = this.getViewLocation();
        const size = this.getViewSize();

        return new Envelope({ left: location.x, top: location.y, width: size.width, height: size.height }).intersectsByEnvelop(new Envelope({ left: x, top: y, width: width, height: height }), tolerance);
    }

    localIntersectsByRectangle(x, y, width, height, tolerance = 0) {

        return new Envelope({ left: this.x, top: this.y, width: this.width * this.scale, height: this.height * this.scale }).intersectsByEnvelop(new Envelope({ left: x, top: y, width: width, height: height }), tolerance);
    }

    containsByRectangle(x, y, width, height, tolerance = 0) {

        const location = this.getViewLocation();
        const size = this.getViewSize();

        return new Envelope({ left: location.x, top: location.y, width: size.width, height: size.height }).containsByEnvelop(new Envelope({ left: x, top: y, width: width, height: height }), tolerance);
    }

    withInRectangle(x, y, width, height, tolerance = 0) {

        const location = this.getViewLocation();
        const size = this.getViewSize();

        return new Envelope({ left: x, top: y, width: width, height: height }).containsByEnvelop(new Envelope({ left: location.x, top: location.y, width: size.width, height: size.height }), tolerance);
    }

    findWithInRectangle(x, y, width, height, tolerance = 0) {

        if (this.isFindable && !this.isFindable())
            return null;

        const location = this.getViewLocation();
        const size = this.getViewSize();

        return new Envelope({ left: x, top: y, width: width, height: height }).containsByEnvelop(new Envelope({ left: location.x, top: location.y, width: size.width, height: size.height }), tolerance) ? this : null;
    }
}